/**
 * Utilidad para manejar pagos con MercadoPago
 * 
 * IMPORTANTE: MercadoPago NO permite crear preferencias desde el frontend por seguridad.
 * Las preferencias DEBEN crearse en el backend usando el Access Token.
 * 
 * Esta implementación crea el link de pago en el backend y luego redirige al usuario.
 */

/**
 * Crea un link de pago de MercadoPago a través del backend
 * @param {Array} cartItems - Items del carrito
 * @param {string} uuid - UUID del negocio/comercio
 * @param {string} paymentId - ID único de la compra (opcional)
 * @returns {Promise<{payment_link: string, preference_id: string}>} - Objeto con payment_link y preference_id
 */
export const createMercadoPagoPaymentLink = async (cartItems, uuid, paymentId = '') => {
  // Obtener la URL del backend desde las variables de entorno
  // Usar REACT_APP_BACKEND_URL_COBRO como prioridad
  const backendUrl = process.env.REACT_APP_BACKEND_URL_COBRO || 
                     process.env.REACT_APP_BACKEND_URL || 
                     process.env.REACT_APP_API_URL?.replace('/api', '') || 
                     'http://localhost:5000';
  
  // Obtener la URL base de la aplicación para las back_urls
  const baseUrl = window.location.origin;
  
  try {
    // Transformar cartItems al formato que espera la API
    const productos = cartItems.map(item => ({
      title: item.descripcion || item.nombre || 'Producto',
      quantity: item.quantity || 1,
      unit_price: parseFloat(item.precio) || 0,
      description: item.descripcion || item.nombre || ''
    }));

    // Preparar el body según la especificación de la API
    const requestBody = {
      uuid: uuid,
      productos: productos,
      back_urls: {
        success: `${baseUrl}/success${paymentId ? `?id=${paymentId}` : ''}`,
        failure: `${baseUrl}/error${paymentId ? `?id=${paymentId}` : ''}`,
        pending: `${baseUrl}/success${paymentId ? `?id=${paymentId}` : ''}`
      },
      auto_return: "approved",
      notification_url: `${backendUrl}/mercadopago/webhook`
    };

    // Llamar al endpoint del backend para crear el link de pago
    const response = await fetch(`${backendUrl}/mercadopago/payments/create-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Error al crear el link de pago');
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Error al crear el link de pago');
    }

    return {
      payment_link: data.payment_link,
      preference_id: data.preference_id
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Redirige al usuario al link de pago de MercadoPago
 * @param {string} paymentLink - URL del link de pago
 */
export const redirectToMercadoPago = (paymentLink) => {
  if (paymentLink) {
    window.location.href = paymentLink;
  }
};

